// PROJECT ANALYSIS REPORT
// Project-wise hours breakdown and analysis.
// Phase 7: Reports & Analytics

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Table, Badge, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {getProjectAnalysisReport, ProjectSummary, ProjectBreakdown} from '../../../../api/ReportsAPI'
import ExportButton from './ExportButton'

interface Props {
  startDate: string
  endDate: string
  managerId?: string
}

const ProjectAnalysis: React.FC<Props> = ({startDate, endDate, managerId}) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<ProjectSummary | null>(null)
  const [byProject, setByProject] = useState<ProjectBreakdown[]>([])
  const [byProjectType, setByProjectType] = useState<any[]>([])

  // Filters
  const [projectType, setProjectType] = useState<string>('')

  useEffect(() => {
    if (startDate && endDate) {
      loadReport()
    }
  }, [startDate, endDate, projectType, managerId])

  const loadReport = async () => {
    try {
      setLoading(true)
      const response = await getProjectAnalysisReport({
        startDate,
        endDate,
        projectType: projectType || undefined,
        managerId,
      })

      if (response.data?.statusCode === 200) {
        const data = response.data.data
        setSummary(data.summary)
        setByProject(data.byProject)
        setByProjectType(data.byProjectType)
      }
    } catch (error: any) {
      console.error('Error loading project analysis report:', error)
      toast.error(error.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  const getProjectTypeBadge = (type: string) => {
    return type === 'REAL' ? 'primary' : 'secondary'
  }

  const getUtilizationColor = (rate: number) => {
    if (rate >= 90) return 'success'
    if (rate >= 75) return 'info'
    if (rate >= 60) return 'warning'
    return 'danger'
  }

  if (loading) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border text-primary' />
        <p className='mt-2 text-muted'>Loading report...</p>
      </div>
    )
  }

  if (!summary) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-chart-pie fa-3x text-muted mb-3'></i>
        <p className='text-muted'>No data available for selected period</p>
      </div>
    )
  }

  return (
    <>
      {/* Filters and Export */}
      <Row className='mb-3'>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Project Type</Form.Label>
            <Form.Select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
              <option value=''>All Project Types</option>
              <option value='REAL'>Real Projects</option>
              <option value='INTERNAL'>Internal Projects</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={8} className='d-flex align-items-end justify-content-end'>
          <ExportButton
            reportType='project-analysis'
            filters={{
              startDate,
              endDate,
              projectType: projectType || undefined,
              managerId,
            }}
          />
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card className='border-primary'>
            <Card.Body className='text-center'>
              <h4 className='text-primary mb-0'>{summary.totalProjects}</h4>
              <small className='text-muted'>Total Projects</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-info'>
            <Card.Body className='text-center'>
              <h4 className='text-info mb-0'>{summary.totalHours}h</h4>
              <small className='text-muted'>Total Hours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-success'>
            <Card.Body className='text-center'>
              <h4 className='text-success mb-0'>{summary.totalBillableHours}h</h4>
              <small className='text-muted'>Billable Hours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-warning'>
            <Card.Body className='text-center'>
              <h4 className='text-warning mb-0'>{summary.averageHoursPerProject}h</h4>
              <small className='text-muted'>Avg Hours/Project</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Project Type Distribution */}
      <Row className='mb-4'>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className='mb-0'>Project Type Distribution</h5>
            </Card.Header>
            <Card.Body>
              <Table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Projects</th>
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {byProjectType.map((type, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge bg={getProjectTypeBadge(type.type)}>{type.type}</Badge>
                      </td>
                      <td>{type.projectCount}</td>
                      <td>{type.totalHours}h</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Simple Pie Chart Visualization */}
              <div className='d-flex justify-content-center mt-3'>
                {byProjectType.map((type, idx) => {
                  const percentage = summary.totalHours > 0
                    ? ((type.totalHours / summary.totalHours) * 100).toFixed(1)
                    : 0
                  const color = type.type === 'REAL' ? '#0d6efd' : '#6c757d'

                  return (
                    <div key={idx} className='text-center mx-3'>
                      <div
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '50%',
                          background: `conic-gradient(${color} 0% ${percentage}%, #e9ecef ${percentage}% 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {percentage}%
                      </div>
                      <small className='mt-2 d-block'>{type.type}</small>
                    </div>
                  )
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className='mb-0'>Quick Stats</h5>
            </Card.Header>
            <Card.Body>
              <Row className='text-center'>
                <Col>
                  <h3 className='text-primary'>{summary.realProjects}</h3>
                  <small className='text-muted'>Real Projects</small>
                </Col>
                <Col>
                  <h3 className='text-secondary'>{summary.internalProjects}</h3>
                  <small className='text-muted'>Internal Projects</small>
                </Col>
              </Row>
              <hr />
              <div className='mt-3'>
                <div className='d-flex justify-content-between mb-2'>
                  <span>Billable Hours</span>
                  <strong>{summary.totalBillableHours}h</strong>
                </div>
                <div className='d-flex justify-content-between mb-2'>
                  <span>Total Hours</span>
                  <strong>{summary.totalHours}h</strong>
                </div>
                <div className='d-flex justify-content-between'>
                  <span>Billable Rate</span>
                  <strong>
                    {summary.totalHours > 0
                      ? ((summary.totalBillableHours / summary.totalHours) * 100).toFixed(1)
                      : 0}
                    %
                  </strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Project Breakdown Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className='mb-0'>Project Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Project Code</th>
                    <th>Project Name</th>
                    <th>Type</th>
                    <th>Billable Hours</th>
                    <th>Actual Hours</th>
                    <th>Utilization</th>
                    <th>Employees</th>
                    <th>Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {byProject.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='text-center text-muted'>
                        No projects found for selected criteria
                      </td>
                    </tr>
                  ) : (
                    byProject.map((project) => (
                      <tr key={project.projectId}>
                        <td>
                          <strong>{project.projectCode}</strong>
                        </td>
                        <td>{project.projectName}</td>
                        <td>
                          <Badge bg={getProjectTypeBadge(project.projectType)}>
                            {project.projectType}
                          </Badge>
                        </td>
                        <td>{project.billableHours}h</td>
                        <td>{project.actualHours}h</td>
                        <td>
                          <Badge bg={getUtilizationColor(project.utilizationRate)}>
                            {project.utilizationRate}%
                          </Badge>
                        </td>
                        <td>
                          <Badge bg='info'>{project.employeeCount}</Badge>
                        </td>
                        <td>{project.entriesCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className='table-secondary'>
                  <tr>
                    <th colSpan={3}>Total</th>
                    <th>{summary.totalBillableHours}h</th>
                    <th>{summary.totalHours}h</th>
                    <th colSpan={3}>-</th>
                  </tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ProjectAnalysis

